import React, { Children, Fragment, forwardRef, isValidElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import './AppSelect.css';

const OPTION_ANCHOR = { to: 'bottom start', gap: 6, padding: 8 };

const textFromChildren = (children) => Children.toArray(children).map((child) => {
  if (typeof child === 'string' || typeof child === 'number') return String(child);
  return isValidElement(child) ? textFromChildren(child.props.children) : '';
}).join('');

/** Interpret the same option/optgroup children used by existing native selects. */
function parseSelectChildren(children, inheritedDisabled = false, group = null) {
  const entries = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) {
      entries.push(...parseSelectChildren(child.props.children, inheritedDisabled, group));
    } else if (child.type === 'optgroup') {
      const label = String(child.props.label ?? '');
      const key = `${entries.length}-${child.key ?? label}`;
      entries.push({ kind: 'group', key, label });
      entries.push(...parseSelectChildren(child.props.children, inheritedDisabled || child.props.disabled, key));
    } else if (child.type === 'option') {
      const label = child.props.label ?? textFromChildren(child.props.children);
      entries.push({
        kind: 'option',
        key: child.key,
        value: String(child.props.value ?? textFromChildren(child.props.children)),
        label,
        disabled: Boolean(inheritedDisabled || child.props.disabled),
        hidden: Boolean(child.props.hidden),
        title: child.props.title,
        group,
      });
    }
  });
  return entries;
}

/** A value snapshot keeps asynchronous handlers' target.value stable, too. */
function createSelectEvent(type, element, value, originalEvent = null) {
  const target = element ? new Proxy(element, {
    get(node, key) {
      if (key === 'value') return String(value);
      if (key === 'type') return 'select-one';
      const item = Reflect.get(node, key, node);
      return typeof item === 'function' ? item.bind(node) : item;
    },
  }) : { value: String(value) };
  let defaultPrevented = false;
  let propagationStopped = false;
  return {
    type,
    target,
    currentTarget: target,
    relatedTarget: originalEvent?.relatedTarget ?? null,
    nativeEvent: originalEvent?.nativeEvent ?? originalEvent,
    bubbles: true,
    cancelable: true,
    timeStamp: originalEvent?.timeStamp ?? Date.now(),
    get defaultPrevented() { return defaultPrevented; },
    preventDefault() { defaultPrevented = true; originalEvent?.preventDefault?.(); },
    stopPropagation() { propagationStopped = true; originalEvent?.stopPropagation?.(); },
    isDefaultPrevented: () => defaultPrevented,
    isPropagationStopped: () => propagationStopped,
    persist() {},
  };
}

/**
 * Single-select, native-shaped API with a custom, portal-mounted listbox.
 * className/style/ref belong to the trigger itself; no layout wrapper is added.
 */
export const AppSelect = forwardRef(function AppSelect({
  children,
  value,
  defaultValue,
  onChange,
  onInput,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  disabled = false,
  className = '',
  style,
  id,
  name,
  form,
  displayValue,
  placeholder,
  required,
  ...buttonProps
}, forwardedRef) {
  const { multiple: _multiple, size: _size, ...triggerProps } = buttonProps;
  const generatedId = useId();
  const triggerId = id || `app-select-${generatedId}`;
  const buttonRef = useRef(null);
  const optionsRef = useRef(null);
  const focusWithin = useRef(false);
  const initialDefault = useRef(defaultValue == null ? undefined : String(defaultValue));
  const [localValue, setLocalValue] = useState(() => defaultValue == null ? undefined : String(defaultValue));
  const entries = parseSelectChildren(children);
  const options = entries.filter((entry) => entry.kind === 'option');
  const requestedValue = value === undefined ? localValue : String(value ?? '');
  const selected = options.find((option) => option.value === requestedValue)
    ?? options.find((option) => !option.disabled);
  const selectedValue = selected?.value ?? '';
  const uncontrolled = value === undefined;

  useEffect(() => {
    if (!uncontrolled) return;
    const parentForm = buttonRef.current?.form;
    if (!parentForm) return;
    const reset = (event) => {
      // Native form reset changes an uncontrolled value without emitting change.
      queueMicrotask(() => {
        if (!event.defaultPrevented) setLocalValue(initialDefault.current);
      });
    };
    parentForm.addEventListener('reset', reset);
    return () => parentForm.removeEventListener('reset', reset);
  }, [form, uncontrolled]);

  const setButtonRef = useCallback((node) => {
    buttonRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);
  const containsFocus = (node) => Boolean(node && (
    buttonRef.current?.contains(node) || optionsRef.current?.contains(node)
  ));
  const handleFocus = (event) => {
    if (focusWithin.current) return;
    focusWithin.current = true;
    onFocus?.(createSelectEvent('focus', buttonRef.current, selectedValue, event));
  };
  const handleBlur = (event) => {
    // Moving into the portalled options is still inside this control.
    if (containsFocus(event.relatedTarget)) return;
    focusWithin.current = false;
    onBlur?.(createSelectEvent('blur', buttonRef.current, selectedValue, event));
  };
  const handleChange = (nextValue) => {
    const next = String(nextValue);
    if (disabled || next === selectedValue) return;
    if (value === undefined) setLocalValue(next);
    onInput?.(createSelectEvent('input', buttonRef.current, next));
    onChange?.(createSelectEvent('change', buttonRef.current, next));
  };
  const handleTriggerKeyDown = (event) => {
    onKeyDown?.(event);
    // Canvas shortcuts used to recognize SELECT; they must not consume a listbox key.
    if (event.key !== 'Escape' || buttonRef.current?.getAttribute('aria-expanded') === 'true') event.stopPropagation();
  };
  const handleTriggerKeyUp = (event) => {
    onKeyUp?.(event);
    event.stopPropagation();
  };
  const stopPortalEvent = (event) => event.stopPropagation();

  return (
    <Listbox value={selectedValue} onChange={handleChange} disabled={disabled} name={name} form={form}>
      <ListboxButton
        {...triggerProps}
        id={triggerId}
        ref={setButtonRef}
        type="button"
        name={name}
        form={form}
        value={selectedValue}
        aria-required={required || undefined}
        className={`app-select ${className}`}
        style={style}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleTriggerKeyDown}
        onKeyUp={handleTriggerKeyUp}
      >
        <span className="app-select__value">{displayValue ?? selected?.label ?? placeholder ?? '\u00a0'}</span>
        <svg className="app-select__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </ListboxButton>
      <ListboxOptions
        ref={optionsRef}
        anchor={OPTION_ANCHOR}
        portal
        modal={false}
        className="app-select__options"
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseDown={stopPortalEvent}
        onPointerDown={stopPortalEvent}
        onClick={stopPortalEvent}
        onKeyDown={stopPortalEvent}
        onKeyUp={stopPortalEvent}
      >
        {entries.map((entry, index) => entry.kind === 'group' ? (
          <div key={`group-${index}`} id={`${triggerId}-group-${entry.key}`} className="app-select__group" role="presentation">{entry.label}</div>
        ) : entry.hidden ? null : (
          <ListboxOption
            key={`option-${entry.key ?? index}-${entry.value}`}
            value={entry.value}
            disabled={entry.disabled}
            aria-describedby={entry.group == null ? undefined : `${triggerId}-group-${entry.group}`}
            title={entry.title}
            className="app-select__option"
          >
            <span>{entry.label}</span>
            <svg className="app-select__check" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="m3 7 2.5 2.5L11 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
});

export default AppSelect;
